import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { colors, spacing, typography, borderRadius } from '../../tokens';

export interface SegmentedControlOption {
  /**
   * Unique identifier for the option
   */
  value: string;

  /**
   * Label text to display
   */
  label: string;

  /**
   * Whether this option is disabled
   */
  disabled?: boolean;
}

export interface SegmentedControlProps {
  /**
   * Array of options to display
   */
  options: SegmentedControlOption[];

  /**
   * Currently selected option value
   */
  value: string;

  /**
   * Callback when an option is selected
   */
  onChange: (value: string) => void;

  /**
   * Whether the entire control is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  testID,
}) => {
  return (
    <View
      style={styles.container}
      testID={testID}
      accessibilityRole="tablist"
    >
      <View style={styles.buttonsContainer}>
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isDisabled = disabled || option.disabled;

          return (
            <Pressable
              key={option.value}
              onPress={() => !isDisabled && onChange(option.value)}
              disabled={isDisabled}
              style={[
                styles.option,
                index === 0 && styles.firstOption,
                index === options.length - 1 && styles.lastOption,
                isSelected && styles.selectedOption,
                isDisabled && styles.disabledOption,
              ]}
              accessibilityRole="tab"
              accessibilityState={{ selected: isSelected, disabled: isDisabled }}
              testID={`${testID}-option-${option.value}`}
            >
              {isSelected && <View style={styles.selectedBackground} />}
              <Text
                style={[
                  styles.label,
                  isSelected && styles.selectedLabel,
                  isDisabled && styles.disabledLabel,
                ]}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

SegmentedControl.displayName = 'SegmentedControl';

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.neutral[200], // #ECEEF1
    borderRadius: borderRadius.full, // 100px for full pill shape
    padding: spacing.xxs, // 4px padding around buttons
    alignSelf: 'flex-start',
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 0,
  },
  option: {
    flex: 1,
    paddingVertical: spacing.xs, // 8px vertical padding (32px height - text)
    paddingHorizontal: spacing.lg, // 20px horizontal padding
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: 32,
  },
  firstOption: {
    borderTopLeftRadius: borderRadius.full,
    borderBottomLeftRadius: borderRadius.full,
  },
  lastOption: {
    borderTopRightRadius: borderRadius.full,
    borderBottomRightRadius: borderRadius.full,
  },
  selectedOption: {
    // No additional styles needed, background handled by selectedBackground
  },
  selectedBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.neutral[0], // white #FCFCFC
    borderRadius: borderRadius.lg, // 20px
    shadowColor: colors.neutral[900],
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  disabledOption: {
    opacity: 0.5,
  },
  label: {
    ...typography.body.md,
    fontWeight: '400',
    color: colors.neutral[900], // #020202
    zIndex: 1,
  },
  selectedLabel: {
    color: colors.neutral[900], // #020202
    fontWeight: '400',
  },
  disabledLabel: {
    opacity: 0.5,
  },
});
